import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getMe } from '../api/client';
import { useTokenSource } from '../api/token';
import { effectiveLabel, formatRange } from '../review';
import { readRememberedReviewSession, useReviewStore } from '../session/review';
import { useSessionStore } from '../session/store';
import { confirmedClips, useRenderStore } from '../session/render';
import { useExportStore } from '../session/export';
import { h264ProfileName } from '../render';

/**
 * Export route — render the CONFIRMED clips to MP4 and hand them over.
 *
 * The screen exists to make three things visible that are otherwise invisible:
 *   1. what this browser can actually do (the capability probe, §2.5) and which renderer that
 *      selects (§2.3);
 *   2. one clip at a time through the queue — "Rendering 3 of 12 clips…" (§6);
 *   3. the metrics D1 recorded for each render (renderer, render_time_ms, size_bytes).
 *
 * Nothing unconfirmed is ever offered here (plan §D4).
 */
export function ExportRoute() {
  const tokens = useTokenSource();
  const [params, setParams] = useSearchParams();

  const reviewSessionId = useReviewStore((state) => state.sessionId);
  const session = useReviewStore((state) => state.session);
  const clips = useReviewStore((state) => state.clips);
  const media = useReviewStore((state) => state.media);
  const loading = useReviewStore((state) => state.loading);
  const load = useReviewStore((state) => state.load);
  const storeSessionId = useSessionStore((state) => state.sessionId);

  const report = useRenderStore((state) => state.report);
  const rendererKind = useRenderStore((state) => state.rendererKind);
  const branding = useRenderStore((state) => state.branding);
  const running = useRenderStore((state) => state.running);
  const progress = useRenderStore((state) => state.progress);
  const items = useRenderStore((state) => state.items);
  const jobs = useRenderStore((state) => state.jobs);
  const error = useRenderStore((state) => state.error);
  const serverUnavailable = useRenderStore((state) => state.serverUnavailable);
  const probe = useRenderStore((state) => state.probe);
  const loadBranding = useRenderStore((state) => state.loadBranding);
  const runAll = useRenderStore((state) => state.runAll);
  const download = useRenderStore((state) => state.download);
  const downloadAll = useRenderStore((state) => state.downloadAll);

  const [token, setToken] = useState<string | null>(null);
  const [tokenAttempt, setTokenAttempt] = useState(0);
  const [quality, setQuality] = useState<'preview' | 'export'>('export');
  const [userId, setUserId] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState<number>(0);

  // EXPORT lane (card §1-§3): the tier decides the watermark, shares carry the rendered bytes,
  // and a reel is one stitched MP4 with one link.
  const usage = useExportStore((state) => state.usage);
  const upgradePrompt = useExportStore((state) => state.upgradePrompt);
  const createdShares = useExportStore((state) => state.createdShares);
  const sharingClipId = useExportStore((state) => state.sharingClipId);
  const reelSelection = useExportStore((state) => state.reelSelection);
  const reelTitle = useExportStore((state) => state.reelTitle);
  const reelRendering = useExportStore((state) => state.reelRendering);
  const reelProgress = useExportStore((state) => state.reelProgress);
  const reelResult = useExportStore((state) => state.reelResult);
  const reelError = useExportStore((state) => state.reelError);
  const loadUsage = useExportStore((state) => state.loadUsage);
  const shareClip = useExportStore((state) => state.shareClip);
  const toggleReelClip = useExportStore((state) => state.toggleReelClip);
  const setReelTitle = useExportStore((state) => state.setReelTitle);
  const buildReel = useExportStore((state) => state.buildReel);
  const downloadReel = useExportStore((state) => state.downloadReel);
  const resetReel = useExportStore((state) => state.resetReel);
  const clearUpgradePrompt = useExportStore((state) => state.clearUpgradePrompt);

  useEffect(() => {
    let cancelled = false;
    void tokens.getToken().then((value) => {
      if (cancelled) return;
      setToken(value);
      setTokenAttempt((attempt) => attempt + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  const requestedId =
    params.get('session') ?? readRememberedReviewSession() ?? storeSessionId ?? null;

  // Restore the session + its clips from D1 (the review store owns that fetch).
  useEffect(() => {
    if (!requestedId || tokenAttempt === 0) return;
    if (reviewSessionId === requestedId && clips.length > 0) return;
    void load(requestedId, token);
  }, [requestedId, tokenAttempt, reviewSessionId, clips.length, load, token]);

  // Probe at session start (§2.5) and load the coach's branding for the overlay (§5).
  useEffect(() => {
    if (tokenAttempt === 0) return;
    void probe(token);
  }, [tokenAttempt, probe, token]);

  useEffect(() => {
    if (tokenAttempt === 0) return;
    void getMe(token)
      .then((me) => setUserId(me.user?.id ?? null))
      .catch(() => setUserId(null));
  }, [tokenAttempt, token]);

  useEffect(() => {
    if (!userId) return;
    void loadBranding(userId, token);
  }, [userId, loadBranding, token]);

  // The tier/usage drives the watermark and every limit prompt on this screen (card §2, §4).
  useEffect(() => {
    if (tokenAttempt === 0) return;
    void loadUsage(token);
  }, [tokenAttempt, loadUsage, token]);

  const confirmed = useMemo(() => confirmedClips(clips), [clips]);
  const completed = useMemo(() => items.filter((item) => item.result), [items]);

  const onShareClip = useCallback(
    (clipId: string) => {
      const clip = confirmed.find((entry) => entry.id === clipId);
      const item = items.find((entry) => entry.clipId === clipId);
      if (!clip) return;
      void shareClip(clip, item?.result?.blob ?? null, token, {
        expiresInDays,
        durationSeconds: item?.result?.duration,
      });
    },
    [confirmed, items, shareClip, token, expiresInDays],
  );

  const onBuildReel = useCallback(() => {
    void buildReel({
      title: reelTitle || 'Highlight reel',
      clips,
      sourceVideo: media?.url ?? '',
      branding,
      quality,
      token,
    });
  }, [buildReel, reelTitle, clips, media?.url, branding, quality, token]);

  const onRenderAll = useCallback(() => {
    if (!requestedId || confirmed.length === 0) return;
    const sourceVideo = media?.url;
    if (!sourceVideo) return;
    void runAll({
      sessionId: requestedId,
      clips,
      sourceVideo,
      quality,
      token,
      userId,
    });
  }, [requestedId, confirmed.length, media?.url, runAll, clips, quality, token, userId]);

  // E2E / integration seam: the whole render path is drivable without scraping the DOM.
  useEffect(() => {
    const seam = {
      store: useRenderStore,
      probe,
      runAll,
      download,
      downloadAll,
      renderAll: onRenderAll,
      state: () => {
        const state = useRenderStore.getState();
        return {
          rendererKind: state.rendererKind,
          report: state.report,
          running: state.running,
          progress: state.progress?.label ?? null,
          items: state.items.map((item) => ({
            clipId: item.clipId,
            title: item.title,
            status: item.status,
            error: item.error ?? null,
            sizeBytes: item.result?.sizeBytes ?? null,
            duration: item.result?.duration ?? null,
            renderTimeMs: item.result?.renderTimeMs ?? null,
            videoCodec: item.result?.videoCodec ?? null,
            audioTrack: item.result?.audioTrack ?? null,
            renderer: item.result?.renderer ?? null,
          })),
        };
      },
      blobs: () =>
        useRenderStore.getState().items.map((item) => ({
          clipId: item.clipId,
          title: item.title,
          blob: item.result?.blob ?? null,
        })),
    };
    (window as unknown as { __calloutRender?: unknown }).__calloutRender = seam;
    return () => {
      delete (window as unknown as { __calloutRender?: unknown }).__calloutRender;
    };
  }, [probe, runAll, download, downloadAll, onRenderAll]);

  /**
   * EXPORT seam (card §1-§3): shares and reels are worker-backed and asynchronous, so the harness
   * drives them here rather than racing a button click against a render.
   */
  useEffect(() => {
    const seam = {
      store: useExportStore,
      loadUsage,
      shareClip: onShareClip,
      /** The clips the render queue works from (the review store's, confirmed-only filtering and all). */
      renderClips: () => clips,
      /** The signed source-video URL the renderer streams from. */
      mediaUrl: () => media?.url ?? '',
      brandingKit: () => branding,
      /** Share a rendered clip by id and resolve with the created link (or null). */
      shareRenderedClip: async (clipId: string, title?: string, days = 0) => {
        const clip = confirmed.find((entry) => entry.id === clipId);
        const item = items.find((entry) => entry.clipId === clipId);
        if (!clip) return null;
        return shareClip(clip, item?.result?.blob ?? null, token, {
          title,
          expiresInDays: days,
          durationSeconds: item?.result?.duration,
        });
      },
      selectReel: (clipIds: string[]) => useExportStore.getState().setReelSelection(clipIds),
      buildReel: async (clipIds: string[], title: string) => {
        useExportStore.getState().setReelSelection(clipIds);
        useExportStore.getState().setReelTitle(title);
        return buildReel({
          title,
          clips,
          sourceVideo: media?.url ?? '',
          branding,
          quality,
          token,
        });
      },
      state: () => {
        const state = useExportStore.getState();
        return {
          tier: state.usage?.tier ?? null,
          watermark: state.usage?.subscription?.watermark ?? null,
          limits: state.usage?.usage ?? null,
          upgradePrompt: state.upgradePrompt,
          shares: state.shares.map((share) => ({
            id: share.id,
            token: share.token,
            kind: share.kind,
            pageUrl: share.urls.pageUrl,
            accessCount: share.accessCount,
            expiresAt: share.expiresAt,
          })),
          createdShares: state.createdShares,
          reelSelection: state.reelSelection,
          reelTitle: state.reelTitle,
          reelRendering: state.reelRendering,
          reelResult: state.reelResult
            ? {
                duration: state.reelResult.duration,
                segmentCount: state.reelResult.segmentCount,
                sizeBytes: state.reelResult.sizeBytes,
                watermark: state.reelResult.watermark,
                shareUrl: state.reelResult.shareUrl,
                token: state.reelResult.token,
                segments: state.reelResult.segments,
              }
            : null,
          reelError: state.reelError,
        };
      },
      reelBlob: () => useExportStore.getState().reelResult?.blob ?? null,
    };
    (window as unknown as { __calloutExport?: unknown }).__calloutExport = seam;
    return () => {
      delete (window as unknown as { __calloutExport?: unknown }).__calloutExport;
    };
  }, [
    loadUsage,
    onShareClip,
    shareClip,
    buildReel,
    confirmed,
    clips,
    items,
    token,
    media?.url,
    branding,
    quality,
  ]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Export</h1>
        <p className="mt-1 text-sm text-charcoal-500">
          Confirmed clips render on this device with WebCodecs, one at a time, and download as MP4.
        </p>
      </header>

      <form
        data-testid="export-session-form"
        className="flex flex-wrap items-end gap-2 rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (requestedId) setParams({ session: requestedId }, { replace: false });
        }}
      >
        <p className="font-mono text-xs text-charcoal-700" data-testid="export-session-id">
          session {requestedId ?? 'none'}
        </p>
        <Link to="/review" className="text-xs text-copper-600 underline" data-testid="export-to-review">
          back to review
        </Link>
      </form>

      <section
        data-testid="capability-panel"
        data-renderer={rendererKind ?? 'unknown'}
        data-webcodecs={report?.webcodecs ?? 'unknown'}
        className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4 text-sm"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
          This browser
        </h2>
        {report ? (
          <ul className="mt-2 space-y-1 font-mono text-xs text-charcoal-700">
            <li data-testid="capability-level">webcodecs {report.webcodecs}</li>
            <li data-testid="capability-video">
              h264 {report.videoEncode.join(', ') || 'none'} (
              {h264ProfileName(report.videoEncode[0] ?? null) ?? 'none'})
            </li>
            <li data-testid="capability-audio">audio {report.audioEncode.join(', ') || 'none'}</li>
            <li data-testid="capability-renderer">renderer {report.renderer}</li>
            {report.warnings.map((warning) => (
              <li key={warning} className="text-copper-700" data-testid="capability-warning">
                {warning}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="capability-pending">
            Probing this browser…
          </p>
        )}
        <p className="mt-3 text-xs text-charcoal-500" data-testid="branding-summary">
          branding:{' '}
          {branding
            ? `${branding.clubName ?? 'user kit'} · ${branding.primaryColor} / ${branding.secondaryColor}${branding.logoUrl ? ' · logo' : ''}`
            : 'Callout defaults'}
        </p>
      </section>

      <section
        data-testid="tier-panel"
        data-tier={usage?.tier ?? 'unknown'}
        data-watermark={usage?.subscription?.watermark ? 'true' : 'false'}
        className="rounded-lg border border-charcoal-500/20 bg-white/60 p-4 text-sm"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
          Your plan
        </h2>
        {usage ? (
          <ul className="mt-2 space-y-1 font-mono text-xs text-charcoal-700">
            <li data-testid="tier-name">tier {usage.tier}</li>
            <li data-testid="tier-watermark">
              watermark {usage.subscription.watermark ? 'on (Made with Callout)' : 'off'}
            </li>
            <li data-testid="tier-sessions">
              sessions this week {usage.usage.sessionsThisWeek}/{usage.usage.sessionsThisWeekLimit}
            </li>
            <li data-testid="tier-shares">
              active share links {usage.usage.activeShareLinks}/{usage.usage.activeShareLinkLimit}
            </li>
            <li data-testid="tier-reels">
              reels this week {usage.usage.reelsThisWeek}/{usage.usage.reelsPerWeekLimit}
            </li>
          </ul>
        ) : (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="tier-pending">
            Loading plan…
          </p>
        )}
        <label className="mt-3 block text-xs text-charcoal-700">
          share link expiry
          <select
            data-testid="share-expiry"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
            className="ml-2 rounded border border-charcoal-500/30 px-2 py-1"
          >
            <option value={0}>never</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
          </select>
        </label>
        {usage && usage.tier === 'free' && (
          <p className="mt-2 text-xs text-charcoal-500" data-testid="watermark-note">
            Free tier exports carry the &ldquo;Made with Callout&rdquo; watermark. Pro removes it.
          </p>
        )}
      </section>

      {upgradePrompt && (
        <p
          data-testid="upgrade-prompt"
          className="rounded-lg border border-copper bg-copper-300/20 px-4 py-3 text-sm text-charcoal-800"
        >
          {upgradePrompt}
          <button
            type="button"
            data-testid="upgrade-dismiss"
            onClick={() => clearUpgradePrompt()}
            className="ml-3 rounded border border-charcoal-500/30 px-2 py-0.5 text-xs font-semibold"
          >
            Dismiss
          </button>
        </p>
      )}

      {loading && (
        <p className="text-sm text-charcoal-500" data-testid="export-loading">
          Loading session…
        </p>
      )}

      {serverUnavailable && (
        <p
          role="alert"
          className="rounded-lg border border-copper bg-copper-300/20 px-4 py-3 text-sm text-charcoal-800"
          data-testid="server-unavailable"
        >
          {serverUnavailable} — this browser has no H.264 encoder, so clips need server rendering,
          which is not switched on yet.
        </p>
      )}

      {error && !serverUnavailable && (
        <p role="alert" className="text-sm font-medium text-red-700" data-testid="export-error">
          {error}
        </p>
      )}

      {!loading && session && confirmed.length === 0 && (
        <p className="text-sm text-charcoal-500" data-testid="export-empty">
          No confirmed clips. The model proposes, the coach decides — confirm a clip in review
          first.
        </p>
      )}

      {confirmed.length > 0 && (
        <section
          data-testid="export-queue"
          data-confirmed-count={confirmed.length}
          className="space-y-3"
        >
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3">
            <button
              type="button"
              data-testid="render-all"
              onClick={onRenderAll}
              disabled={running}
              className="rounded bg-charcoal px-3 py-1.5 text-sm font-semibold text-chalk disabled:opacity-50"
            >
              {running
                ? 'Rendering…'
                : `Render ${confirmed.length} confirmed clip${confirmed.length === 1 ? '' : 's'}`}
            </button>
            <label className="text-xs text-charcoal-700">
              quality
              <select
                data-testid="export-quality"
                value={quality}
                onChange={(event) => setQuality(event.target.value as 'preview' | 'export')}
                className="ml-2 rounded border border-charcoal-500/30 px-2 py-1"
              >
                <option value="export">export</option>
                <option value="preview">preview</option>
              </select>
            </label>
            {progress && (
              <p className="font-mono text-xs text-charcoal-700" data-testid="render-progress">
                {progress.label} ({Math.round(progress.clipFraction * 100)}%)
              </p>
            )}
            {completed.length > 0 && (
              <button
                type="button"
                data-testid="download-zip"
                onClick={() => void downloadAll()}
                className="ml-auto rounded bg-copper px-3 py-1.5 text-sm font-semibold text-charcoal"
              >
                Download all {completed.length} (ZIP)
              </button>
            )}
          </div>

          <ul className="space-y-2" data-testid="export-clips">
            {confirmed.map((clip) => {
              const item = items.find((entry) => entry.clipId === clip.id);
              const job = jobs[clip.id];
              return (
                <li
                  key={clip.id}
                  data-testid="export-clip"
                  data-clip-id={clip.id}
                  data-status={item?.status ?? 'queued'}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-charcoal-500/20 bg-white/60 px-4 py-3"
                >
                  <span className="text-sm font-semibold" data-testid="export-clip-title">
                    {effectiveLabel(clip)}
                  </span>
                  <span className="font-mono text-xs text-charcoal-500" data-testid="export-clip-range">
                    {formatRange(clip)}
                  </span>
                  {item?.status === 'complete' && item.result && (
                    <>
                      <span
                        className="font-mono text-xs text-charcoal-700"
                        data-testid="export-clip-metrics"
                      >
                        {(item.result.sizeBytes / 1024).toFixed(0)} KB ·{' '}
                        {item.result.duration.toFixed(2)}s · {item.result.renderTimeMs} ms ·{' '}
                        {item.result.renderer}
                        {item.result.videoCodec
                          ? ` ${h264ProfileName(item.result.videoCodec)}/${item.result.videoCodec}`
                          : ''}
                        {` · audio ${item.result.audioTrack}`}
                      </span>
                      <button
                        type="button"
                        data-testid="download-clip"
                        onClick={() => download(clip.id)}
                        className="ml-auto rounded bg-charcoal px-3 py-1 text-xs font-semibold text-chalk"
                      >
                        Download MP4
                      </button>
                      <button
                        type="button"
                        data-testid="share-clip"
                        disabled={sharingClipId === clip.id}
                        onClick={() => onShareClip(clip.id)}
                        className="rounded bg-copper px-3 py-1 text-xs font-semibold text-charcoal disabled:opacity-50"
                      >
                        {sharingClipId === clip.id ? 'Sharing…' : 'Share'}
                      </button>
                    </>
                  )}
                  {createdShares[clip.id] && (
                    <a
                      data-testid="share-clip-url"
                      data-share-token={createdShares[clip.id]!.token}
                      href={createdShares[clip.id]!.pageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full break-all font-mono text-[10px] text-copper-700 underline"
                    >
                      {createdShares[clip.id]!.pageUrl}
                    </a>
                  )}
                  <label className="flex items-center gap-1 text-[10px] text-charcoal-600">
                    <input
                      type="checkbox"
                      data-testid="reel-select"
                      data-clip-id={clip.id}
                      checked={reelSelection.includes(clip.id)}
                      onChange={() => toggleReelClip(clip.id)}
                    />
                    reel
                  </label>
                  {item?.status === 'rendering' && (
                    <span
                      className="font-mono text-xs text-copper-700"
                      data-testid="export-clip-rendering"
                    >
                      rendering…
                    </span>
                  )}
                  {item?.status === 'failed' && (
                    <span
                      className="text-xs font-medium text-red-700"
                      data-testid="export-clip-error"
                    >
                      {item.error}
                    </span>
                  )}
                  {job && (
                    <span
                      className="font-mono text-[10px] text-charcoal-500"
                      data-testid="export-clip-job"
                    >
                      D1 job {job.status}
                      {job.render_time_ms ? ` · ${job.render_time_ms} ms` : ''}
                      {job.size_bytes ? ` · ${job.size_bytes} B` : ''}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {confirmed.length >= 3 && (
        <section
          data-testid="reel-builder"
          data-selected={reelSelection.length}
          className="space-y-3 rounded-lg border border-charcoal-500/20 bg-white/60 p-4"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal-500">
            Create highlight reel
          </h2>
          <p className="text-xs text-charcoal-500">
            Tick 3 or more confirmed clips above, give the reel a title, and Callout stitches them
            into one MP4 with one share link.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs text-charcoal-700">
              title
              <input
                data-testid="reel-title"
                value={reelTitle}
                onChange={(event) => setReelTitle(event.target.value)}
                placeholder="Round 3 Defensive Highlights"
                className="ml-2 w-64 rounded border border-charcoal-500/30 px-2 py-1"
              />
            </label>
            <button
              type="button"
              data-testid="create-reel"
              disabled={reelRendering || reelSelection.length < 3}
              onClick={onBuildReel}
              className="rounded bg-copper px-3 py-1.5 text-sm font-semibold text-charcoal disabled:opacity-50"
            >
              {reelRendering
                ? `Stitching ${reelProgress?.index ?? 1} of ${reelProgress?.total ?? reelSelection.length}…`
                : `Create Highlight Reel (${reelSelection.length})`}
            </button>
            {reelSelection.length > 0 && (
              <button
                type="button"
                data-testid="reel-reset"
                onClick={() => resetReel()}
                className="rounded border border-charcoal-500/30 px-3 py-1.5 text-xs font-semibold text-charcoal-700"
              >
                Clear selection
              </button>
            )}
          </div>

          {reelError && (
            <p role="alert" className="text-xs font-medium text-red-700" data-testid="reel-error">
              {reelError}
            </p>
          )}

          {reelResult && (
            <div
              data-testid="reel-result"
              data-duration={reelResult.duration}
              data-segment-count={reelResult.segmentCount}
              data-size-bytes={reelResult.sizeBytes}
              data-watermark={reelResult.watermark ? 'true' : 'false'}
              className="space-y-2 rounded border border-copper/40 bg-copper-300/10 p-3"
            >
              <p className="font-mono text-xs text-charcoal-700">
                {reelResult.segmentCount} clips · {reelResult.duration.toFixed(2)}s ·{' '}
                {(reelResult.sizeBytes / 1024).toFixed(0)} KB · {reelResult.videoCodec ?? 'unknown'}
                {reelResult.watermark ? ' · watermarked' : ''}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="download-reel"
                  onClick={() => downloadReel()}
                  className="rounded bg-charcoal px-3 py-1 text-xs font-semibold text-chalk"
                >
                  Download reel MP4
                </button>
                {reelResult.shareUrl && (
                  <a
                    data-testid="reel-share-url"
                    data-share-token={reelResult.token ?? ''}
                    href={reelResult.shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all font-mono text-[10px] text-copper-700 underline"
                  >
                    {reelResult.shareUrl}
                  </a>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
